import { Heart, Bookmark, Share2, MapPin, Play, ExternalLink } from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import PostMenu from './PostMenu';
import EditPostModal from './EditPostModal';
import DeletePostModal from './DeletePostModal';
import SharePostModal from './SharePostModal';

import { getMediaUrl } from '@/utils/mediaUrl';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Extract YouTube video ID from various URL formats
const getYouTubeVideoId = (url) => {
  if (!url) return null;
  const patterns = [
    /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([a-zA-Z0-9_-]{11})/,
    /youtube\.com\/shorts\/([a-zA-Z0-9_-]{11})/
  ];
  for (const pattern of patterns) {
    const match = url.match(pattern);
    if (match) return match[1];
  }
  return null;
};

// YouTube Thumbnail Component with Play Button
const YouTubeThumbnail = ({ url }) => {
  const [showPlayer, setShowPlayer] = useState(false);
  const videoId = getYouTubeVideoId(url);
  
  if (!videoId) {
    return (
      <div className="aspect-video bg-gray-900 flex items-center justify-center">
        <p className="text-white/70">Invalid YouTube URL</p>
      </div>
    );
  }
  
  const thumbnailUrl = `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`;
  const youtubeUrl = `https://www.youtube.com/watch?v=${videoId}`;
  
  // If user clicked play, try to show embedded player
  if (showPlayer) {
    return (
      <div className="aspect-video bg-gray-900 relative">
        <iframe
          src={`https://www.youtube-nocookie.com/embed/${videoId}?autoplay=1&rel=0`}
          className="w-full h-full"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
          title="YouTube video"
        />
        {/* Fallback link if embed fails */}
        <a
          href={youtubeUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="absolute bottom-2 right-2 flex items-center gap-1 px-3 py-1.5 bg-red-600 hover:bg-red-700 text-white text-xs font-medium rounded-full transition-colors"
        >
          <ExternalLink className="w-3 h-3" />
          Open in YouTube
        </a>
      </div>
    );
  }
  
  // Show thumbnail with play button
  return (
    <div className="aspect-video bg-gray-900 relative group cursor-pointer" onClick={() => setShowPlayer(true)}>
      {/* Thumbnail Image */}
      <img
        src={thumbnailUrl}
        alt="YouTube video thumbnail"
        className="w-full h-full object-cover"
        onError={(e) => {
          // Fallback to lower quality thumbnail
          e.target.src = `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`;
        }}
      />
      
      {/* Dark Overlay */}
      <div className="absolute inset-0 bg-black/30 group-hover:bg-black/40 transition-colors" />
      
      {/* Play Button */}
      <div className="absolute inset-0 flex items-center justify-center">
        <div className="w-16 h-16 sm:w-20 sm:h-20 bg-red-600 hover:bg-red-700 rounded-full flex items-center justify-center shadow-lg transform group-hover:scale-110 transition-transform">
          <Play className="w-8 h-8 sm:w-10 sm:h-10 text-white ml-1" fill="white" />
        </div>
      </div>
      
      {/* YouTube Badge */}
      <div className="absolute top-3 left-3 flex items-center gap-1.5 px-2 py-1 bg-black/70 rounded text-white text-xs">
        <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
          <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
        </svg>
        YouTube
      </div>
      
      {/* Watch on YouTube Link */}
      <a
        href={youtubeUrl}
        target="_blank"
        rel="noopener noreferrer"
        onClick={(e) => e.stopPropagation()}
        className="absolute bottom-3 right-3 flex items-center gap-1 px-3 py-1.5 bg-white/90 hover:bg-white text-gray-900 text-xs font-medium rounded-full transition-colors"
      >
        <ExternalLink className="w-3 h-3" />
        Watch on YouTube
      </a>
    </div>
  );
};

export const EnhancedPostCard = ({ post: initialPost, currentUserId, onPostUpdate, onPostDelete }) => {
  const navigate = useNavigate();
  const [post, setPost] = useState(initialPost);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [imageError, setImageError] = useState(false);

  const isOwner = currentUserId === post.user_id;
  const mediaUrl = getMediaUrl(post.media_url);
  const isUploadedMedia = post.media_url?.startsWith('/uploads/') || post.media_url?.startsWith('/api/uploads/');
  const isYouTube = post.media_url?.includes('youtube.com') || post.media_url?.includes('youtu.be');

  const handleLike = async () => {
    try {
      const token = localStorage.getItem('dharmbook_token');
      const response = await axios.post(
        `${API}/posts/${post.id}/like`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setPost(prev => ({
        ...prev,
        is_liked: response.data.liked,
        likes_count: response.data.liked ? prev.likes_count + 1 : prev.likes_count - 1
      }));
    } catch (error) {
      toast.error('Failed to like post');
    }
  };

  const handleSave = async () => {
    try {
      const token = localStorage.getItem('dharmbook_token');
      const response = await axios.post(
        `${API}/posts/${post.id}/save`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setPost(prev => ({ ...prev, is_saved: response.data.saved }));
      toast.success(response.data.saved ? 'Post saved' : 'Post unsaved');
    } catch (error) {
      toast.error('Failed to save post');
    }
  };

  const handleEdit = async (formData) => {
    try {
      const token = localStorage.getItem('dharmbook_token');
      await axios.put(
        `${API}/posts/${post.id}`,
        {
          content: formData.content,
          post_type: post.post_type,
          media_url: formData.media_url,
          mantra_meaning: formData.mantra_meaning
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setPost(prev => ({
        ...prev,
        content: formData.content,
        media_url: formData.media_url,
        mantra_meaning: formData.mantra_meaning,
        is_edited: true
      }));
      
      if (onPostUpdate) onPostUpdate();
      toast.success('Post updated successfully');
    } catch (error) {
      toast.error('Failed to update post');
    }
  };

  const handleDelete = async () => {
    try {
      const token = localStorage.getItem('dharmbook_token');
      console.log('Deleting post:', post.id);
      
      const response = await axios.delete(`${API}/posts/${post.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      console.log('Delete response:', response.data);

      if (onPostDelete) {
        console.log('Calling onPostDelete callback');
        onPostDelete(post.id);
      }
      toast.success('Post deleted');
      setShowDeleteModal(false);
    } catch (error) {
      console.error('Delete error:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete post');
    }
  };

  return (
    <>
      <div className="bg-white rounded-lg sm:rounded-xl shadow-sm border border-gray-200 overflow-hidden w-full">
        {/* Post Header */}
        <div className="flex items-center justify-between p-3 sm:p-4">
          <button
            onClick={() => navigate(`/profile/${post.user_id}`)}
            className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1 cursor-pointer text-left group"
            data-testid={`post-header-${post.id}`}
          >
            <img
              src={post.user_avatar}
              alt={post.user_name}
              className="w-9 h-9 sm:w-10 sm:h-10 rounded-full object-cover flex-shrink-0 group-hover:ring-2 group-hover:ring-[#FF9933]/40 transition-all"
            />
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-1 sm:gap-2 flex-wrap">
                <h3 className="font-semibold text-gray-900 text-sm sm:text-base truncate group-hover:text-[#FF9933] transition-colors">{post.user_name}</h3>
                {post.is_official && (
                  <span className="px-1.5 sm:px-2 py-0.5 bg-gradient-to-r from-[#FF9933] to-[#FF6600] text-white text-[10px] sm:text-xs rounded font-medium flex-shrink-0">Official</span>
                )}
                {post.user_role === 'pandit' && !post.is_official && (
                  <span className="px-1.5 sm:px-2 py-0.5 bg-[#FF9933] text-white text-[10px] sm:text-xs rounded font-medium flex-shrink-0">Verified</span>
                )}
                {post.user_role === 'creator' && !post.is_official && (
                  <span className="px-1.5 sm:px-2 py-0.5 bg-purple-500 text-white text-[10px] sm:text-xs rounded font-medium flex-shrink-0">Creator</span>
                )}
              </div>
              <div className="flex items-center gap-2 text-[10px] sm:text-xs text-gray-500">
                <span>
                  {new Date(post.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                </span>
                {post.location?.city && (
                  <span className="flex items-center gap-0.5">
                    <MapPin className="w-3 h-3" />
                    {post.location.city}
                  </span>
                )}
                {post.is_edited && <span>(Edited)</span>}
              </div>
            </div>
          </button>
          
          <PostMenu 
            post={post} 
            isOwner={isOwner} 
            onEdit={() => setShowEditModal(true)}
            onDelete={() => setShowDeleteModal(true)}
          />
        </div>

        {/* Content */}
        <div className="px-3 sm:px-4 pb-3">
          {/* Mantra Post */}
          {post.post_type === 'mantra' && (
            <div className="bg-gradient-to-br from-orange-50 to-amber-50 rounded-lg sm:rounded-xl p-4 sm:p-6 mb-3 border border-orange-100">
              <p className="text-base sm:text-lg text-gray-800 text-center mb-2 font-semibold" style={{ fontFamily: 'Merriweather, serif' }}>
                {post.content}
              </p>
              {post.mantra_meaning && (
                <p className="text-xs sm:text-sm text-gray-600 text-center italic">Meaning: {post.mantra_meaning}</p>
              )}
            </div>
          )}

          {/* Text/Image Post Content */}
          {(post.post_type === 'text' || post.post_type === 'image') && post.content && (
            <p className="text-sm sm:text-base text-gray-800 leading-relaxed mb-3 break-words whitespace-pre-wrap">{post.content}</p>
          )}

          {/* Video Post Content */}
          {post.post_type === 'video' && post.content && (
            <p className="text-sm sm:text-base text-gray-800 leading-relaxed mb-3 break-words">{post.content}</p>
          )}

          {/* Audio Post Content */}
          {post.post_type === 'audio' && post.content && (
            <p className="text-sm sm:text-base text-gray-800 leading-relaxed mb-3 break-words">{post.content}</p>
          )}
        </div>

        {/* Media Display */}
        {mediaUrl && !imageError && (
          <div className="relative mx-3 sm:mx-4 rounded-xl overflow-hidden">
            {/* Uploaded Image */}
            {(post.media_type === 'image' || (post.post_type === 'image' && isUploadedMedia) || (post.post_type === 'text' && !isYouTube && !post.media_url?.includes('youtube'))) && (
              <img
                src={mediaUrl}
                alt="Post media"
                className="w-full max-h-[500px] object-contain bg-gray-100"
                onError={() => setImageError(true)}
              />
            )}

            {/* Uploaded Video */}
            {(post.media_type === 'video' && isUploadedMedia) && (
              <video
                src={mediaUrl}
                controls
                className="w-full max-h-[500px] bg-black"
                preload="metadata"
              />
            )}

            {/* YouTube Video - Show thumbnail with play button */}
            {(post.post_type === 'video' && isYouTube) && (
              <YouTubeThumbnail url={post.media_url} />
            )}

            {/* Audio Player */}
            {post.post_type === 'audio' && mediaUrl && (
              <div className="py-3">
                <audio controls className="w-full">
                  <source src={mediaUrl} />
                </audio>
              </div>
            )}
          </div>
        )}

        {/* Stats */}
        <div className="px-3 sm:px-4 pt-3 sm:pt-4 pb-2 flex items-center justify-between text-xs sm:text-sm text-gray-500">
          <span>{post.likes_count} Namaste</span>
        </div>

        {/* Actions */}
        <div className="border-t border-gray-200 px-1 sm:px-2 py-1 flex items-center justify-around">
          <button
            onClick={handleLike}
            data-testid={`like-btn-${post.id}`}
            className={`flex items-center gap-1 sm:gap-2 px-2 sm:px-4 py-2 rounded-lg hover:bg-gray-50 transition-colors flex-1 justify-center ${
              post.is_liked ? 'text-[#FF9933]' : 'text-gray-600'
            }`}
          >
            <Heart
              className="w-4 h-4 sm:w-5 sm:h-5"
              fill={post.is_liked ? '#FF9933' : 'none'}
              stroke={post.is_liked ? '#FF9933' : 'currentColor'}
            />
            <span className="text-xs sm:text-sm font-medium">Namaste</span>
          </button>

          <button
            onClick={handleSave}
            data-testid={`save-btn-${post.id}`}
            className={`flex items-center gap-1 sm:gap-2 px-2 sm:px-4 py-2 rounded-lg hover:bg-gray-50 transition-colors flex-1 justify-center ${
              post.is_saved ? 'text-[#FF9933]' : 'text-gray-600'
            }`}
          >
            <Bookmark
              className="w-4 h-4 sm:w-5 sm:h-5"
              fill={post.is_saved ? '#FF9933' : 'none'}
              stroke={post.is_saved ? '#FF9933' : 'currentColor'}
            />
            <span className="text-xs sm:text-sm font-medium">Save</span>
          </button>

          <button
            onClick={() => setShowShareModal(true)}
            data-testid={`share-btn-${post.id}`}
            className="flex items-center gap-1 sm:gap-2 px-2 sm:px-4 py-2 rounded-lg hover:bg-gray-50 transition-colors text-gray-600 flex-1 justify-center"
          >
            <Share2 className="w-4 h-4 sm:w-5 sm:h-5" />
            <span className="text-xs sm:text-sm font-medium">Share</span>
          </button>
        </div>
      </div>

      {/* Modals */}
      <EditPostModal 
        open={showEditModal} 
        onClose={() => setShowEditModal(false)}
        post={post}
        onSave={handleEdit}
      />

      <DeletePostModal 
        open={showDeleteModal} 
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleDelete}
      />

      <SharePostModal 
        open={showShareModal} 
        onClose={() => setShowShareModal(false)}
        post={post}
      />
    </>
  );
};

export default EnhancedPostCard;
